import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { Suspense, useEffect, useMemo } from 'react';
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import './App.css';
import Layout from './components/layout/Layout';
import store from './store';
import { createMsalConfig } from './config/authConfig';
import PrivateRoute from './routeGuard/PrivateRoute';
import PublicRoute from './routeGuard/PublicRoute';
import ROUTE_SOURCES from './constants/routeSource';
import { Spinner } from './components/loader/view/Loader';
import { ToastContainer } from 'react-toastify';
import { getTimezoneRequest, tenantConfigurationRequest } from './modules/common/services/commonActions';
import { LOGIN_SOURCES } from './constants/loginConstants';
import { GOOGLE_CALLBACK_PATH, LOGIN_PATH } from './constants/routes';
import { GOOGLE_STATE_STORAGE_KEY } from './config/googleConfig';
import { ProviderThemeProvider } from './contexts/ProviderThemeContext';

function AppContent() {
  const dispatch = useDispatch();
  const { tenantConfiguration, isTenantConfigurationLoading, tenantConfigurationError } = useSelector((state) => state.common);
  const { userData } = useSelector((state) => state.auth);
  const entraConfiguration = tenantConfiguration?.find(
    (config) => config?.Source === LOGIN_SOURCES.MICROSOFT_ENTRAID
  );
  const entraClientId = entraConfiguration?.AppClientId;
  const entraTenantId = entraConfiguration?.AppTenantId;
  const msalInstance = useMemo(
    () => new PublicClientApplication(createMsalConfig({
      AppClientId: entraClientId,
      AppTenantId: entraTenantId,
    })),
    [entraClientId, entraTenantId]
  );
  const tenantId = userData?.TenantId || userData?.tenantId || 0;

  // Commented to not replace words based on hierarchy
  // useEffect(() => {
  //   applyHierarchyAliasGlobalPatches();
  // }, []);

  useEffect(() => {
    let serverUrl = window.location.href;
    if (window.location.pathname.toLowerCase() === GOOGLE_CALLBACK_PATH) {
      try {
        serverUrl = JSON.parse(sessionStorage.getItem(GOOGLE_STATE_STORAGE_KEY))?.serverUrl ||
          `${window.location.origin}${LOGIN_PATH}`;
      } catch {
        serverUrl = `${window.location.origin}${LOGIN_PATH}`;
      }
    }
    dispatch(tenantConfigurationRequest(serverUrl));
  }, [dispatch]);

  useEffect(() => {
    if (tenantId) {
      dispatch(getTimezoneRequest(tenantId));
    }
  }, [dispatch, tenantId]);

  const renderRoutes = () => {
    // Suspense is scoped to the routed page only, so Layout/Sidebar never unmount
    // (and lose scroll/collapse state) while a lazy chunk for a new page loads.
    return (
      <Routes>
        {ROUTE_SOURCES.map(({ path, component: Component, isPublic, allowAuthenticated = false, isLoginPage = false, isShowSidebar = true, roles }, index) => (
          <Route
            key={path}
            path={path}
            element={
              isPublic ? (
                <PublicRoute allowAuthenticated={allowAuthenticated}>
                  <Layout isShowSidebar={false} isLoginPage={isLoginPage} isPublic={isPublic} roles={roles}>
                    <Suspense fallback={<Spinner />}><Component /></Suspense>
                    <ToastContainer />
                  </Layout>
                </PublicRoute>
              ) : (
                <PrivateRoute>
                  <Layout isShowSidebar={isShowSidebar} roles={roles}>
                    <Suspense fallback={<Spinner />}><Component /></Suspense>
                    <ToastContainer />
                  </Layout>
                </PrivateRoute>
              )
            }
          />
        ))}
      </Routes>
    )
  };

  if (isTenantConfigurationLoading || (typeof tenantConfiguration === 'undefined' && !tenantConfigurationError)) {
    return <Spinner fullPage />;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <ProviderThemeProvider
          key={`${userData?.TenantId || userData?.tenantId || 0}:${userData?.UserId}:${userData?.RoleCode}`}
          userData={userData}
        >
          {renderRoutes()}
        </ProviderThemeProvider>
      </BrowserRouter>
    </MsalProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;